import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import DialogHeader from "../components/DialogHeader"
import { updateCRMSettings } from "../reducer"
import { A2PFailureList } from "./A2PFailureList"
import {
  BusinessInfoFields,
  EMPTY_BUSINESS,
  EMPTY_SOLE_PROP,
  SolePropFields,
  businessErrors,
  businessPayload,
  solePropErrors,
  solePropPayload,
} from "./A2PFields"
import A2PFormErrors, { a2pErrorMessages } from "./A2PFormErrors"
import { BUSINESS_TYPES } from "./a2pValidation"

function pick(source, template) {
  const result = { ...template }
  Object.keys(template).forEach((key) => {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      result[key] = Array.isArray(source[key])
        ? source[key].join(", ")
        : String(source[key])
    }
  })
  return result
}

/**
 * Edit the business details behind a rejected brand and resubmit the SAME
 * brand (no new registration, subaccount or fee beyond Twilio's own rules).
 */
export default function ResubmitBrandDialog({
  open,
  onClose,
  failures,
  soleProprietor,
}) {
  const axios = useAuthorizedAxiosClient()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState([])
  const [business, setBusiness] = useState(EMPTY_BUSINESS)
  const [soleProp, setSoleProp] = useState(EMPTY_SOLE_PROP)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState([])
  const highlight = (failures || []).flatMap((failure) =>
    (failure.fields || []).map((field) => field.name)
  )

  useEffect(
    () => {
      if (!open) {
        return
      }
      setLoading(true)
      setLoadError([])
      setErrors([])
      axios
        .get("/crm/api/twilio/a2p-10dlc/registration-details/")
        .then(({ data }) => {
          if (soleProprietor) {
            setSoleProp(
              pick(
                {
                  ...data.business,
                  ...data.address,
                  ...data.trust,
                  mobile_phone_number:
                    data.trust?.mobile_phone_number ||
                    data.business?.phone_number,
                },
                EMPTY_SOLE_PROP
              )
            )
          } else {
            const picked = pick(data.business, EMPTY_BUSINESS)
            // Older registrations may hold values Twilio no longer accepts
            // ("Limited Liability", DUNS): make the client pick again.
            if (!BUSINESS_TYPES.includes(picked.business_type)) {
              picked.business_type = ""
            }
            if (
              !["EIN", "CBN"].includes(picked.business_registration_identifier)
            ) {
              picked.business_registration_identifier = "EIN"
            }
            setBusiness(picked)
          }
        })
        .catch((error) => setLoadError(a2pErrorMessages(error)))
        .finally(() => setLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  )

  const formErrors = soleProprietor
    ? solePropErrors(soleProp)
    : businessErrors(business)
  const canSubmit = !loading && Object.keys(formErrors).length === 0

  function submit() {
    setSubmitting(true)
    setErrors([])
    const body = soleProprietor
      ? { sole_prop: solePropPayload(soleProp) }
      : { business: businessPayload(business) }
    axios
      .post("/crm/api/twilio/a2p-10dlc/resubmit-brand/", body)
      .then(({ data }) => {
        dispatch(updateCRMSettings(data))
        onClose(true)
      })
      .catch((error) => {
        const results = error?.response?.data?.twilio_error
        if (Array.isArray(results) && results.length) {
          setErrors(
            results
              .filter((result) => !result.passed)
              .flatMap((result) =>
                (result.fields || [])
                  .filter((field) => !field.passed)
                  .map(
                    (field) =>
                      `${
                        field.friendly_name || result.requirement_friendly_name
                      }: ${field.failure_reason}`
                  )
              )
          )
        } else {
          setErrors(a2pErrorMessages(error))
        }
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogHeader title="Edit & resubmit registration" onClose={onClose} />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          {failures?.length > 0 && (
            <Alert severity="error" sx={{ textAlign: "left" }}>
              <A2PFailureList failures={failures} />
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Correct the highlighted details so they match your official records
            exactly, then resubmit. Your first 3 resubmissions are free.
          </Typography>
          {loading ? (
            <Column sx={{ alignItems: "center", py: 4 }}>
              <CircularProgress />
            </Column>
          ) : soleProprietor ? (
            <SolePropFields
              value={soleProp}
              onChange={setSoleProp}
              highlight={highlight}
            />
          ) : (
            <BusinessInfoFields
              value={business}
              onChange={setBusiness}
              highlight={highlight}
            />
          )}
          <A2PFormErrors messages={[...loadError, ...errors]} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => onClose()}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={!canSubmit}
          onClick={submit}
        >
          Resubmit registration
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
